import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/**
 * The whole `web.*` surface: plain HTTP to any public address, the same for every enterprise. The worker's web
 * dispatcher switches exhaustively over this union, and the registry below is typed by it, so neither can gain a
 * name the other lacks.
 *
 * Nothing here names a site. A host, path, header or response shape that belongs to one system is task data or a
 * bound vendor tool, never part of this family.
 */
export const WEB_TOOL_NAMES = ['web.read', 'web.sitemap', 'web.request'] as const

export type WebToolName = (typeof WEB_TOOL_NAMES)[number]

/** The methods `web.request` sends, served as its `method` enum and read by the handler, so the two cannot differ. */
export const WEB_REQUEST_METHODS = ['GET'] as const

/**
 * The request headers a model may set on `web.request`. An allowlist, because a credential header cannot be
 * enumerated to refuse: a credential never comes from the model.
 */
export const WEB_REQUEST_HEADERS = ['accept', 'accept-language'] as const

const NOT_THE_CONTENT =
    'Nothing was returned: this is not the content. Do not summarise it, hash it or train memory from this call. The size and sha256 in payload describe the whole content.'

const SHARED_ERRORS: readonly BuiltInToolError[] = [
    {
        code: 'web_url_invalid',
        when: '`url` is missing, not a URL, longer than 2,048 characters, not http or https, carries a username or password, or names a port.',
        handling: 'tool_error',
    },
    {
        code: 'web_address_refused',
        when: 'The host is, or resolves to, a private, loopback, link-local, shared, unique-local, reserved or metadata address, or is a local or internal name.',
        handling: 'tool_error',
    },
    {
        code: 'web_redirect_refused',
        when: 'There are more than 5 redirects, a redirect loops or names no location, or one leads to a URL or address this tool refuses. The refused address is never requested.',
        handling: 'tool_error',
    },
    {
        code: 'web_fetch_failed',
        when: 'The name did not resolve, the connection failed, or the response used an encoding the tool does not read.',
        handling: 'tool_error',
    },
    {
        code: 'web_credential_insecure',
        when: 'The URL, or a redirect, reaches over plain http a host the enterprise attached a credential to. A credential is only ever sent over https, so that address is never requested.',
        handling: 'tool_error',
    },
    {
        code: 'web_credential_unavailable',
        when: 'The credentials attached to this task’s layers could not be read, for example a secret that no longer decrypts. Nothing is requested, and the message never carries the secret.',
        handling: 'tool_error',
    },
    { code: 'web_timeout', when: 'No complete response arrived within 20 seconds.', handling: 'tool_error' },
    {
        code: 'web_response_too_large',
        when: 'The response passed 2 MB once decompressed. Nothing past that was read.',
        handling: 'tool_error',
    },
]

const STATUS_NOT_OK: BuiltInToolError = {
    code: 'web_status_not_ok',
    when: 'The response status is outside 2xx, so the body is an error page and not the content.',
    handling: 'tool_error',
}

const URL_ONLY = (description: string): JsonSchema => ({
    type: 'object',
    properties: { url: { type: 'string', description } },
    required: ['url'],
    additionalProperties: false,
})

/** The `web.*` definitions, keyed by name so a missing one is a type error. */
export const WEB_TOOLS: { readonly [Name in WebToolName]: BuiltInTool & { name: Name } } = {
    'web.read': {
        name: 'web.read',
        family: 'web',
        summary: 'Read one public web page as visible text, with a hash that changes only when the text does.',
        description:
            "Read one public web page as text. Pass url (http or https). Returns the page's visible text, with scripts, styles and markup removed and whitespace collapsed to single spaces, its title, its share tags (share: the title, description, image, address, site name and type its head declares for link previews, each image and address absolute), and sha256: the hash of exactly the text. The same page gives the same sha256 until its words change, so use it to tell whether a page changed, and as a memory.train source revision. Only HTML is read here: use web.sitemap for a sitemap and web.request for JSON or plain text. A page whose text is too long for one result is refused as web_page_too_large, with its chars, sha256 and share tags and no text: that is not the page, so do not summarise it or train from it, though its share tags are the page's own. A status outside 2xx is refused as web_status_not_ok. Follows up to 5 redirects. Private, local and internal addresses are refused. No JavaScript runs, and no cookies or credentials are sent.",
        inputSchema: URL_ONLY('The page to read: an http or https URL on a public host.'),
        returns:
            '`{ kind: "page", url, finalUrl, status, contentType, title, share, text, chars, sha256 }`. `finalUrl` is where the redirects ended. `title` is null when the page has none, and is not part of `text`. `share` is `{ title?, description?, image?, url?, siteName?, type? }` from the head\'s Open Graph tags, falling back to `twitter:image`, the meta description and the canonical link; a field the page does not declare is left out, and `image` and `url` are absolute http or https addresses. `chars` is the length of `text`, and `sha256` is 64 hex characters over exactly `text`.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        retryNote: 'A repeat sends the request again; a GET changes nothing.',
        errors: [
            ...SHARED_ERRORS,
            STATUS_NOT_OK,
            {
                code: 'web_not_html',
                when: 'The response is not an HTML page. The error names its content type.',
                handling: 'tool_error',
                hint: 'web.read reads HTML pages only. Use web.sitemap for a sitemap, or web.request for JSON, XML or plain text.',
            },
            {
                code: 'web_page_too_large',
                when: 'The page’s text does not fit in one result. The error carries `chars`, `sha256` and `share`, and no text.',
                handling: 'tool_error',
                hint: NOT_THE_CONTENT,
            },
        ],
        examples: [{ input: { url: 'https://example.com/pricing' } }],
        public: true,
    },
    'web.sitemap': {
        name: 'web.sitemap',
        family: 'web',
        summary: 'Read an XML sitemap and return its URLs as a list.',
        description:
            'Read an XML sitemap and return its entries. Pass url. A urlset returns kind sitemap with total and urls: each loc, and its lastmod when the sitemap gives one. A sitemap index returns kind sitemap_index with sitemaps: call web.sitemap again on each loc, because this tool does not follow them. complete is true when every entry is in the list. When the list is too long for one result, complete is false, total says how many entries there are, and no entries are returned: that is not an empty sitemap. Anything that is not a sitemap is refused as web_not_sitemap, and a status outside 2xx as web_status_not_ok. Private, local and internal addresses are refused.',
        inputSchema: URL_ONLY('The sitemap’s http or https URL.'),
        returns:
            '`{ kind: "sitemap", url, finalUrl, complete, total, urls: [{ loc, lastmod? }] }`, or `{ kind: "sitemap_index", url, finalUrl, complete, total, sitemaps: [{ loc, lastmod? }] }`. Entries are in document order, exactly as the sitemap gives them. `lastmod` is left out when the sitemap gives none. When `complete` is false there is no list.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        retryNote: 'A repeat sends the request again; a GET changes nothing.',
        errors: [
            ...SHARED_ERRORS,
            STATUS_NOT_OK,
            {
                code: 'web_not_sitemap',
                when: 'The document is neither a urlset nor a sitemap index.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { url: 'https://example.com/sitemap.xml' } }],
        public: true,
    },
    'web.request': {
        name: 'web.request',
        family: 'web',
        summary: 'Send one GET to a public URL and return the text or JSON body, with its status and hash.',
        description:
            'Send one HTTP request to a public URL and return the response body as text. method is GET, the default and the only method today. Pass url, and headers only to set accept or accept-language. Never pass credentials: authorization, cookie and every other header are refused. Returns status, contentType, body, bytes and sha256 of the body bytes. A status outside 2xx is returned as a result, not an error: check status before you use body. Only text and JSON responses are returned: anything else is refused as web_not_text, and a body too long for one result as web_body_too_large, with its bytes and sha256 and no body. Follows up to 5 redirects. Private, local and internal addresses are refused. To read a page as text use web.read; for a sitemap use web.sitemap.',
        inputSchema: {
            type: 'object',
            properties: {
                url: { type: 'string', description: 'An http or https URL on a public host.' },
                method: {
                    type: 'string',
                    enum: [...WEB_REQUEST_METHODS],
                    description: 'Defaults to GET, the only method today.',
                },
                headers: {
                    type: 'object',
                    properties: {
                        accept: {
                            type: 'string',
                            description: 'The content type to ask for, such as application/json.',
                        },
                        'accept-language': { type: 'string', description: 'The language to ask for, such as en-GB.' },
                    },
                    additionalProperties: false,
                    description: 'Only these two. Credentials are never passed here.',
                },
            },
            required: ['url'],
            additionalProperties: false,
        },
        returns:
            '`{ url, finalUrl, status, contentType, body, bytes, sha256 }`. `body` is the response as text. `bytes` is its size and `sha256` is 64 hex characters over the body bytes, both after decompression.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        retryNote: 'A repeat sends the request again; a GET changes nothing.',
        errors: [
            ...SHARED_ERRORS,
            {
                code: 'web_method_refused',
                when: '`method` is anything other than GET.',
                handling: 'tool_error',
            },
            {
                code: 'web_header_refused',
                when: '`headers` sets anything other than `accept` or `accept-language`, or a value that is not a single line of at most 1,000 characters.',
                handling: 'tool_error',
            },
            {
                code: 'web_not_text',
                when: 'The response is not text or JSON, or names no content type. The error names the type.',
                handling: 'tool_error',
            },
            {
                code: 'web_body_too_large',
                when: 'The body does not fit in one result. The error carries `bytes` and `sha256`, and no body.',
                handling: 'tool_error',
                hint: NOT_THE_CONTENT,
            },
        ],
        examples: [
            { input: { url: 'https://example.com/api/status.json' } },
            {
                input: { url: 'https://example.com/feed', method: 'GET', headers: { accept: 'application/json' } },
                note: 'asking for JSON',
            },
        ],
        public: true,
    },
}
